/**
 * `/fulfill` — the Ship-only worklist (inventory-clarity P2). Ship-only items are goods FSP does NOT
 * produce but DOES stock and ship (decals, stickers, pre-made goods); they never appear in the DTF
 * order queue / batch build or the caps worklist. Grouped BY ORDER (a station packs shipments, not
 * lines — the P5 ship-station shape). Marking a line packed moves it off the worklist (OPEN → PACKED)
 * and writes NO stock movement in v1 (P2b's consume-at-fulfill keys off this event later). Reversible:
 * a just-packed line sits in an "Undo" panel for the rest of the session. Gated on a dashboard sign-in.
 */
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, Inbox, Loader2, PackageCheck, RefreshCw, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PageContainer } from '@/components/page-container';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { capKey, groupCapsByOrder } from './caps-lib';

/** One Ship-only worklist line: the order-line view + its live shelf-stock resolution (P2b-B). */
type FulfillLine = components['schemas']['FulfillLineView'];

const PAGE_LIMIT = 100;

function itemLabel(l: FulfillLine): string {
  return l.variantTitle ? `${l.title} — ${l.variantTitle}` : l.title;
}

export function FulfillPage() {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canPack = state.status === 'signed-in';

  // Cursor-paginated; drain all pages so the whole worklist + the count are accurate.
  const query = useInfiniteQuery({
    queryKey: ['fulfill', 'worklist'],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/fulfill/worklist', {
        params: { query: { limit: PAGE_LIMIT, cursor: pageParam } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the Ship-only worklist.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Session "just packed" list — the reversal affordance for a mis-click (the packed line has left
  // the server worklist, so we keep the row here with an Undo until the page reloads).
  const [justPacked, setJustPacked] = useState<FulfillLine[]>([]);
  const [inFlight, setInFlight] = useState<Set<string>>(() => new Set());
  const justPackedKeys = useMemo(() => new Set(justPacked.map(capKey)), [justPacked]);

  const lines = (query.data?.pages.flatMap((p) => p.items) ?? []) as FulfillLine[];
  // Hide a just-packed line until the refetch drops it, so it never shows in both the worklist and
  // the Undo panel at once.
  const groups = useMemo(
    () => groupCapsByOrder(lines.filter((l) => !justPackedKeys.has(capKey(l)))),
    [lines, justPackedKeys],
  );
  const totalLines = groups.reduce((n, g) => n + g.lines.length, 0);
  const needsMappingCount = groups.reduce(
    (n, g) => n + g.lines.filter((l) => l.needsItemMapping).length,
    0,
  );

  const packMut = useMutation({
    mutationFn: async (vars: { line: FulfillLine; packed: boolean }) => {
      const { line, packed } = vars;
      const res = await client.POST('/fulfill/{storeId}/{orderId}/lines/{lineId}/packed', {
        params: { path: { storeId: line.storeId, orderId: line.orderId, lineId: line.lineId } },
        body: { packed },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to update.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    onMutate: (vars) => {
      setInFlight((s) => new Set(s).add(capKey(vars.line)));
    },
    onSuccess: (_d, vars) => {
      setJustPacked((prev) =>
        vars.packed
          ? [vars.line, ...prev.filter((l) => capKey(l) !== capKey(vars.line))]
          : prev.filter((l) => capKey(l) !== capKey(vars.line)),
      );
    },
    onSettled: (_d, _e, vars) => {
      setInFlight((s) => {
        const n = new Set(s);
        n.delete(capKey(vars.line));
        return n;
      });
      qc.invalidateQueries({ queryKey: ['fulfill'] });
      qc.invalidateQueries({ queryKey: ['order', vars.line.storeId, vars.line.orderId] });
      // Packing now consumes the mapped stock item (P2b-C) — refresh the shelf counts this client
      // shows without waiting for the change-feed round trip.
      qc.invalidateQueries({ queryKey: ['stock-items'] });
    },
  });

  const busy = (l: FulfillLine) => inFlight.has(capKey(l));

  return (
    <PageContainer>
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1">
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <PackageCheck className="size-6 text-muted-foreground" />
              Ship-only
              {totalLines > 0 && <Badge variant="secondary">{totalLines}</Badge>}
            </h1>
            <p className="text-sm text-muted-foreground">
              Stocked items FSP ships without producing (decals, pre-made goods). Pull each from the
              shelf and mark it packed — one order is one shipment.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={cn('size-4', query.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </header>

        {!canPack && (
          <Alert>
            <AlertTitle>Sign in to update packing</AlertTitle>
            <AlertDescription>
              You can view the worklist, but marking a line packed needs a dashboard sign-in.
            </AlertDescription>
          </Alert>
        )}

        {query.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn’t load the Ship-only worklist</AlertTitle>
            <AlertDescription>
              {query.error instanceof Error ? query.error.message : 'Please try again.'}
            </AlertDescription>
          </Alert>
        )}

        {packMut.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn’t update the line — nothing changed</AlertTitle>
            <AlertDescription>
              {packMut.error instanceof Error ? packMut.error.message : 'Please try again.'}
            </AlertDescription>
          </Alert>
        )}

        {needsMappingCount > 0 && (
          <Alert>
            <AlertTitle>
              {needsMappingCount} line{needsMappingCount === 1 ? '' : 's'} without a stock item
            </AlertTitle>
            <AlertDescription>
              These lines don’t say which shelf item they draw from yet. Map each variant to a stock
              item on its{' '}
              <Link
                to="/catalog"
                search={{ filter: 'non-apparel' }}
                className="font-medium underline underline-offset-4"
              >
                catalog page
              </Link>{' '}
              (open the product → Shelf stock). Packing still works — it just won’t be tied to an
              item’s count until mapped.
            </AlertDescription>
          </Alert>
        )}

        {query.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : totalLines === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-16 text-center text-muted-foreground">
            <Inbox className="size-8" />
            <p className="text-sm">Nothing waiting to be packed.</p>
            <p className="max-w-md text-xs">
              Ship-only items appear here once a product is marked “Ship-only” on its catalog page
              and an order for it comes in.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={`${g.storeId}#${g.orderId}`} className="rounded-lg border">
                <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2">
                  <span className="text-sm font-medium">{g.orderName}</span>
                  <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
                    Ship-only — stocked
                  </Badge>
                </div>
                <ul className="divide-y">
                  {g.lines.map((l) => (
                    <li
                      key={capKey(l)}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{itemLabel(l)}</p>
                        <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                          <span>Qty {l.unfulfilledQty}</span>
                          {l.needsItemMapping ? (
                            <Badge
                              variant="outline"
                              className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
                            >
                              Needs item mapping
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">
                              From shelf: {l.stockItemName ?? l.stockItemSlug}
                            </span>
                          )}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        disabled={!canPack || busy(l)}
                        onClick={() => packMut.mutate({ line: l, packed: true })}
                      >
                        {busy(l) ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <Check className="size-4" />
                        )}
                        Mark packed
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {justPacked.length > 0 && (
          <div className="rounded-lg border border-dashed">
            <div className="border-b px-4 py-2 text-sm font-medium text-muted-foreground">
              Just packed ({justPacked.length}) — undo a mis-click
            </div>
            <ul className="divide-y">
              {justPacked.map((l) => (
                <li key={capKey(l)} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm">
                      {l.orderName} · {itemLabel(l)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canPack || busy(l)}
                    onClick={() => packMut.mutate({ line: l, packed: false })}
                  >
                    {busy(l) ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Undo2 className="size-4" />
                    )}
                    Undo
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
